// apps/web/src/features/notifications/utils/routing.ts
//
// Map a notification aggregate to the request-drawer URL that opens
// the related request and (when known) selects the right tab.
//
// China requests open via `/china-rates?openId=<LR-NNNNN>&tab=<tab>`.
// RF requests open via `/russia-rates/<RU-NNNNN>?tab=<tab>`. The tab
// segment is omitted when the aggregate carries `routingTab: null`
// (e.g. responsible-status notifications open the request without
// changing tab). LEAD callbacks open via `/leads/<leadId>` (the episode
// page, #1810) with no tab.
import type {
  NotificationEntityType,
  NotificationEntityId,
  NotificationRoutingTab,
} from '@magimp/shared';

export interface NotificationRoutingTarget {
  entityType: NotificationEntityType;
  entityId: NotificationEntityId;
  routingTab: NotificationRoutingTab | null;
}

export function buildNotificationHref(target: NotificationRoutingTarget): string {
  const tabPart = target.routingTab ? `tab=${encodeURIComponent(target.routingTab)}` : '';
  if (target.entityType === 'CHINA_REQUEST') {
    const id = `openId=${encodeURIComponent(target.entityId)}`;
    const query = tabPart ? `${id}&${tabPart}` : id;
    return `/china-rates?${query}`;
  }
  if (target.entityType === 'RUSSIA_REQUEST') {
    const base = `/russia-rates/${encodeURIComponent(target.entityId)}`;
    return tabPart ? `${base}?${tabPart}` : base;
  }
  // WHY (#1363, FR-5.3; #1810): a LEAD callback routes to the lead's episode
  //   page `/leads/<id>`. No tab — the episode page defaults to the feed tab,
  //   so `routingTab` is intentionally ignored here.
  if (target.entityType === 'LEAD') {
    return `/leads/${encodeURIComponent(target.entityId)}`;
  }
  if (target.entityType === 'CARGO_PROBLEM') {
    return `/cargo/problems/${encodeURIComponent(target.entityId)}`;
  }
  // Fail closed rather than misroute an unknown entity type.
  return '';
}
